import { demoCategoryBands, demoDealStats, demoPriceMoves } from '../dev/buyerDemoData'
import { withDemoUi } from '../dev/demoMode'
import { apiRequest } from './client'

export type PriceMoveDto = {
  catalog_item_id: number
  organization_id: number
  product_type_id: number | null
  unit: string | null
  previous_price: number
  current_price: number
  delta_abs: number
  delta_pct: number | null
  observed_at: string
  is_private: boolean
  previous_observed_at?: string | null
  private_supplier_id?: number | null
  supplier_name?: string | null
  item_name?: string | null
}

export type PriceHistoryPointDto = {
  observed_at: string
  price: number
  source: string | null
}

export type DealStatsBucketDto = {
  key_id: number
  deal_count: number
  total_amount: number
  estimated_savings?: number
}

export type DealStatsDto = {
  deal_count: number
  total_amount: number
  estimated_savings: number
  by_supplier: DealStatsBucketDto[]
  by_category: DealStatsBucketDto[]
}

export type CategoryBandDto = {
  band_date: string
  product_type_id: number
  source_kind: string
  unit: string
  min_price: number
  max_price: number
  avg_price: number
  median_price: number
  sample_count: number
}

export type AnalyticsPeriodDays = 7 | 30 | 90

export type PriceMoveDirection = 'all' | 'down' | 'up'

export type CategoryBandSourceKind = 'list' | 'deal'

const toIsoOffset = (date: Date) => date.toISOString()

const toIsoDate = (date: Date) => date.toISOString().slice(0, 10)

export const analyticsPeriodRange = (days: AnalyticsPeriodDays, now = new Date()) => {
  const to = new Date(now)
  const from = new Date(now)
  from.setUTCDate(from.getUTCDate() - days)
  return { from: toIsoOffset(from), to: toIsoOffset(to) }
}

/** Дефолт коридоров: последние 4 недели. */
export const DEFAULT_BAND_RANGE_DAYS = 28

export const analyticsBandDateRange = (days: number = DEFAULT_BAND_RANGE_DAYS, now = new Date()) => {
  const to = new Date(now)
  const from = new Date(now)
  from.setUTCDate(from.getUTCDate() - days)
  return { from: toIsoDate(from), to: toIsoDate(to) }
}

export type FetchPriceMovesOptions = {
  limit?: number
  direction?: PriceMoveDirection
  privateSupplierId?: number | null
}

export const fetchPriceMoves = (organizationId: number, options: FetchPriceMovesOptions | number = {}) => {
  const normalized: FetchPriceMovesOptions =
    typeof options === 'number' ? { limit: options } : options
  const limit = normalized.limit ?? 50
  const params = new URLSearchParams({
    organizationId: String(organizationId),
    limit: String(limit),
  })
  if (normalized.direction && normalized.direction !== 'all') {
    params.set('direction', normalized.direction)
  }
  if (normalized.privateSupplierId != null) {
    params.set('privateSupplierId', String(normalized.privateSupplierId))
  }
  const demo = demoPriceMoves()
    .filter((move) => {
      if (normalized.direction === 'up' && move.delta_abs <= 0) {
        return false
      }
      if (normalized.direction === 'down' && move.delta_abs >= 0) {
        return false
      }
      if (
        normalized.privateSupplierId != null &&
        move.private_supplier_id != null &&
        move.private_supplier_id !== normalized.privateSupplierId
      ) {
        return false
      }
      return true
    })
    .slice(0, limit)
  return withDemoUi(demo, () =>
    apiRequest<PriceMoveDto[]>(`/buyer/market-analytics/price-moves?${params.toString()}`),
  )
}

export const fetchPriceHistory = (
  organizationId: number,
  catalogItemId: number,
  limit = 90,
) => {
  const params = new URLSearchParams({
    organizationId: String(organizationId),
    catalogItemId: String(catalogItemId),
    limit: String(limit),
  })
  return withDemoUi([], () =>
    apiRequest<PriceHistoryPointDto[]>(
      `/buyer/market-analytics/price-history?${params.toString()}`,
    ),
  )
}

export const fetchMyDealStats = (
  organizationId: number,
  options?: { from?: string; to?: string },
) => {
  const params = new URLSearchParams({ organizationId: String(organizationId) })
  if (options?.from) {
    params.set('from', options.from)
  }
  if (options?.to) {
    params.set('to', options.to)
  }
  return withDemoUi(demoDealStats(), () =>
    apiRequest<DealStatsDto>(`/buyer/market-analytics/my-deal-stats?${params.toString()}`),
  )
}

export const fetchCategoryBands = (
  organizationId: number,
  productTypeId: number,
  options?: { from?: string; to?: string; sourceKind?: CategoryBandSourceKind },
) => {
  const params = new URLSearchParams({
    organizationId: String(organizationId),
    productTypeId: String(productTypeId),
  })
  if (options?.from) {
    params.set('from', options.from)
  }
  if (options?.to) {
    params.set('to', options.to)
  }
  if (options?.sourceKind) {
    params.set('sourceKind', options.sourceKind)
  }
  const demoBands = demoCategoryBands(productTypeId).filter((band) => {
    if (options?.from && band.band_date < options.from) {
      return false
    }
    if (options?.to && band.band_date > options.to) {
      return false
    }
    return true
  })
  return withDemoUi(demoBands, () =>
    apiRequest<CategoryBandDto[]>(
      `/buyer/market-analytics/category-bands?${params.toString()}`,
    ),
  )
}
